import { useIntl } from '@umijs/max';
import { Button, message, notification } from 'antd';
import { stripAllSpaces } from '@/utils/noSpaceInput';
import defaultSettings from '../config/defaultSettings';

const { pwa } = defaultSettings;
const isHttps = document.location.protocol === 'https:';

/**
 * 全局“禁止输入空格”的方案（作用于所有单行 input）
 *
 * 目标：
 * - 让用户在任何输入框里输入空格时，都立即被剔除（输入过程中即时生效）
 * - 避免前后端因为“隐藏空格”导致的各种莫名 bug（查询查不到、保存失败、比对不一致等）
 *
 * 为什么放在 global.tsx：
 * - global.tsx 会在应用启动时执行一次，非常适合做全局能力初始化
 * - 这样不用逐页逐表单改 ProFormText / Input，新增页面也天然生效
 *
 * 设计要点：
 * - 使用 document.addEventListener('input', handler, true)：
 *   - 第三个参数 true 表示“捕获阶段”，可以更早拿到事件，覆盖 antd/pro 的封装组件
 * - 仅处理 HTMLInputElement：
 *   - 不影响 textarea（多行文本常常需要空格）
 *   - 不影响富文本编辑器等复杂输入区域
 * - 兼容中文输入法：
 *   - 组合输入（IME）时 event.isComposing === true，跳过，避免干扰拼音联想
 * - 支持白名单：
 *   - 若极少数输入框必须允许空格，可在该 input 上加 data-allow-space="true"
 */
const setupNoSpaceInputs = () => {
  if (typeof document === 'undefined') return;

  /**
   * input 事件处理器（捕获阶段）
   * - 读取目标 input 的 value
   * - 去除其中所有空白字符
   * - 若发生变更：回写 value，并尽量修正光标位置
   */
  const handler = (event: Event) => {
    const anyEvent = event as any;
    if (anyEvent?.isComposing) return;

    const target = anyEvent?.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.dataset?.allowSpace === 'true') return;

    // 排除不应处理的 input 类型（这些类型本身就不接受或不适合处理文本 value）
    const inputType = (target.getAttribute('type') || 'text').toLowerCase();
    if (
      inputType === 'checkbox' ||
      inputType === 'radio' ||
      inputType === 'button' ||
      inputType === 'submit' ||
      inputType === 'reset' ||
      inputType === 'file' ||
      inputType === 'hidden' ||
      inputType === 'image' ||
      inputType === 'range' ||
      inputType === 'color' ||
      inputType === 'date' ||
      inputType === 'datetime-local' ||
      inputType === 'month' ||
      inputType === 'time' ||
      inputType === 'week'
    ) {
      return;
    }

    const rawValue = target.value;
    const nextValue = stripAllSpaces(rawValue);
    if (nextValue === rawValue) return;

    /**
     * 光标位置修正（核心细节）
     *
     * 问题：直接修改 input.value 会导致光标跳到末尾，输入体验很差
     * 解决：用“变更前光标位置之前的子串”计算出“变更后的等价光标位置”
     *
     * 例子：
     * - rawValue:  "ab c|d"（| 表示光标）
     * - 去空白后: "abc|d"
     * - 光标应当跟随字符移动，而不是跳到末尾
     */
    const selectionStart = target.selectionStart ?? rawValue.length;
    const selectionEnd = target.selectionEnd ?? rawValue.length;
    const nextSelectionStart = (
      stripAllSpaces(rawValue.slice(0, selectionStart)) as string
    ).length;
    const nextSelectionEnd = (
      stripAllSpaces(rawValue.slice(0, selectionEnd)) as string
    ).length;

    target.value = nextValue as string;
    try {
      target.setSelectionRange(nextSelectionStart, nextSelectionEnd);
    } catch {
      // 某些浏览器/输入类型下不允许 setSelectionRange，忽略即可
    }
  };

  // 捕获阶段监听，覆盖绝大多数 UI 组件封装
  document.addEventListener('input', handler, true);
};

// 初始化一次，全局生效
setupNoSpaceInputs();

const clearCache = () => {
  // remove all caches
  if (window.caches) {
    caches
      .keys()
      .then((keys) => {
        keys.forEach((key) => {
          caches.delete(key);
        });
      })
      .catch((e) => console.log(e));
  }
};

// if pwa is true
if (pwa) {
  // Notify user if offline now
  window.addEventListener('sw.offline', () => {
    message.warning(useIntl().formatMessage({ id: 'app.pwa.offline' }));
  });

  // Pop up a prompt on the page asking the user if they want to use the latest version
  window.addEventListener('sw.updated', (event: Event) => {
    const e = event as CustomEvent;
    const reloadSW = async () => {
      // Check if there is sw whose state is waiting in ServiceWorkerRegistration
      // https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerRegistration
      const worker = e.detail?.waiting;
      if (!worker) {
        return true;
      }
      // Send skip-waiting event to waiting SW with MessageChannel
      await new Promise((resolve, reject) => {
        const channel = new MessageChannel();
        channel.port1.onmessage = (msgEvent) => {
          if (msgEvent.data.error) {
            reject(msgEvent.data.error);
          } else {
            resolve(msgEvent.data);
          }
        };
        worker.postMessage({ type: 'skip-waiting' }, [channel.port2]);
      });

      clearCache();
      window.location.reload();
      return true;
    };
    const key = `open${Date.now()}`;
    const btn = (
      <Button
        type="primary"
        onClick={() => {
          notification.destroy(key);
          reloadSW();
        }}
      >
        {useIntl().formatMessage({ id: 'app.pwa.serviceworker.updated.ok' })}
      </Button>
    );
    notification.open({
      message: useIntl().formatMessage({ id: 'app.pwa.serviceworker.updated' }),
      description: useIntl().formatMessage({
        id: 'app.pwa.serviceworker.updated.hint',
      }),
      btn,
      key,
      onClose: async () => null,
    });
  });
} else if ('serviceWorker' in navigator && isHttps) {
  // unregister service worker
  const { serviceWorker } = navigator;
  if (serviceWorker.getRegistrations) {
    serviceWorker.getRegistrations().then((sws) => {
      sws.forEach((sw) => {
        sw.unregister();
      });
    });
  }
  serviceWorker.getRegistration().then((sw) => {
    if (sw) sw.unregister();
  });

  clearCache();
}
